import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Warning,
  ArrowSquareOut,
  DownloadSimple,
  CheckCircle,
  ChatDots,
  Phone,
  EnvelopeSimple,
  PaperPlaneTilt,
  Clock,
  Money,
  X,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatCard } from '@/components/shared/StatCard';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  fetchAgedDebt,
  fetchRecoveryWorkspace,
  addChaseLog,
  type AgedDebtRow,
  type RecoveryWorkspaceItem,
} from '@/lib/feeNotesApi';
import { useAuthStore } from '@/features/auth/store';
import { ScrollLock } from '@/components/shared/ScrollLock';

const fmt = (n: number) =>
  n > 0
    ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '—';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const CHASE_METHODS = [
  { value: 'email', label: 'Email', icon: <EnvelopeSimple className="h-4 w-4" /> },
  { value: 'phone', label: 'Phone', icon: <Phone className="h-4 w-4" /> },
  { value: 'letter', label: 'Letter', icon: <PaperPlaneTilt className="h-4 w-4" /> },
  { value: 'in_person', label: 'In person', icon: <ChatDots className="h-4 w-4" /> },
];

function methodIcon(method: string) {
  const size = 'h-3.5 w-3.5';
  switch (method) {
    case 'email': return <EnvelopeSimple className={size} />;
    case 'phone': return <Phone className={size} />;
    case 'letter': return <PaperPlaneTilt className={size} />;
    case 'in_person': return <ChatDots className={size} />;
    default: return null;
  }
}

function chaseBadge(item: RecoveryWorkspaceItem) {
  if (!item.lastChased) {
    return item.daysOverdue > 14
      ? <Badge variant="danger">Never chased</Badge>
      : <Badge variant="warning">Not yet chased</Badge>;
  }
  const d = item.lastChased.daysSince;
  if (d <= 7) return <Badge variant="success">Chased {d}d ago</Badge>;
  if (d <= 30) return <Badge variant="warning">Chased {d}d ago</Badge>;
  return <Badge variant="danger">Chased {d}d ago</Badge>;
}

function agedRiskBadge(row: AgedDebtRow) {
  if (row.d90 > 5_000 || row.d60 + row.d90 > 10_000) return <Badge variant="danger">High</Badge>;
  if (row.d60 > 0 || row.d30 > 5_000) return <Badge variant="warning">Medium</Badge>;
  return <Badge>Low</Badge>;
}

// ─── Log Chase Modal ────────────────────────────────────────────────────────

interface LogChaseModalProps {
  item: RecoveryWorkspaceItem;
  onClose: () => void;
  onSaved: () => void;
}

function LogChaseModal({ item, onClose, onSaved }: LogChaseModalProps) {
  const [method, setMethod] = useState('email');
  const [notes, setNotes] = useState('');
  const [contactedAt, setContactedAt] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await addChaseLog(item.feeNoteId, { method, notes: notes.trim() || undefined, contactedAt });
      onSaved();
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-lg border border-border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Log chase</p>
            <p className="text-xs text-muted-foreground">{item.feeNoteNumber} · {item.clientName}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">Method</label>
            <div className="grid grid-cols-2 gap-2">
              {CHASE_METHODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMethod(m.value)}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                    method === m.value
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">Date contacted</label>
            <input
              type="date"
              value={contactedAt}
              onChange={(e) => setContactedAt(e.target.value)}
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">
              Notes <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Left voicemail. Requested payment by Friday…"
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? 'Saving…' : 'Log chase'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

type Tab = 'workspace' | 'aged-debt';

export default function RecoveryQueuePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Shared page: mounted at /clerk/recovery and /barrister/recovery, so every link it offers has
  // to be derived from the signed-in member (Rule #6). Both destinations below were hardcoded to
  // /clerk, which would have dead-ended a barrister on the catch-all. The server scopes the rows
  // themselves — a barrister sees only their own matters' debt (fee-notes.service.ts).
  const memberType = useAuthStore((s) => s.user?.memberType);
  const isClerk = memberType === 'senior_clerk' || memberType === 'junior_clerk';
  const isBarrister = ['silk', 'junior', 'pupil', 'door_tenant'].includes(memberType ?? '');
  const basePath = isClerk ? '/clerk' : isBarrister ? '/barrister' : '/admin';
  // The barrister shell calls contacts "clients" and registers no /contacts route.
  const contactPath = (id: string) =>
    isBarrister ? `/barrister/clients/${id}` : `${basePath}/contacts/${id}`;
  const [tab, setTab] = useState<Tab>('workspace');
  const [chasingItem, setChasingItem] = useState<RecoveryWorkspaceItem | null>(null);

  const { data: workspaceData, isLoading: wsLoading, isError: wsError, error: wsErrorObj, refetch: wsRefetch } = useQuery({
    queryKey: ['recovery-workspace'],
    queryFn: fetchRecoveryWorkspace,
    staleTime: 30_000,
  });

  const { data: agedDebtData, isLoading: adLoading, isError: adError, refetch: adRefetch } = useQuery({
    queryKey: ['aged-debt'],
    queryFn: fetchAgedDebt,
    staleTime: 60_000,
  });

  // `RECOVERY_ROLES` excludes pupils and door tenants, so for them this answers 403. Now that the
  // page is also mounted under /barrister, a pupil can type the URL — and a retryable "couldn't
  // load" panel would be a refusal disguised as a failure. Tell the truth instead, the same way
  // the intake inbox does. This changes no access; the server stays authoritative.
  const refused =
    (wsErrorObj as { response?: { status?: number } } | null)?.response?.status === 403;

  const items = workspaceData ?? [];
  const agedRows = agedDebtData ?? [];

  const stats = useMemo(() => {
    const totalOutstanding = items.reduce((s, i) => s + i.outstanding, 0);
    const neverChased = items.filter((i) => !i.lastChased).length;
    const critical = items.filter((i) => i.daysOverdue > 60).reduce((s, i) => s + i.outstanding, 0);
    return { totalOutstanding, neverChased, critical };
  }, [items]);

  function handleChaseSaved() {
    setChasingItem(null);
    void queryClient.invalidateQueries({ queryKey: ['recovery-workspace'] });
  }

  function exportWorkspaceCsv() {
    const headers = ['Fee Note', 'Matter', 'Client', 'Outstanding (£)', 'Days Overdue', 'Status', 'Chase Count', 'Last Chased'];
    const csvRows = items.map((i) => [
      `"${i.feeNoteNumber}"`,
      `"${i.caseName}"`,
      `"${i.clientName}"`,
      i.outstanding.toFixed(2),
      i.daysOverdue,
      i.status,
      i.chaseCount,
      i.lastChased ? fmtDate(i.lastChased.contactedAt) : '',
    ].join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recovery-workspace-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (refused) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Debt recovery"
          subtitle="Track and chase outstanding invoices — per invoice, per client"
        />
        <EmptyState
          icon={<Warning className="h-6 w-6" />}
          title="Debt recovery isn't available to your role"
          description="Chasing outstanding fee notes is restricted to barristers who own the fees, their clerks and chambers staff. This list is not empty — it is not yours to see."
        />
      </div>
    );
  }

  return (
    <>
      {chasingItem && (
        <LogChaseModal
          item={chasingItem}
          onClose={() => setChasingItem(null)}
          onSaved={handleChaseSaved}
        />
      )}

      <div className="space-y-6">
        <PageHeader
          title="Debt recovery"
          subtitle="Track and chase outstanding invoices — per invoice, per client"
          actions={
            tab === 'workspace' ? (
              <Button
                variant="outline"
                size="md"
                leftIcon={<DownloadSimple className="h-4 w-4" />}
                onClick={exportWorkspaceCsv}
                disabled={items.length === 0}
              >
                Export CSV
              </Button>
            ) : undefined
          }
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            icon={<Money className="h-5 w-5" />}
            label="Total outstanding"
            value={
              wsError ? '—'
                : stats.totalOutstanding > 0
                ? `£${stats.totalOutstanding.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
                : '£0'
            }
            hint={wsError ? "Couldn't load" : 'across all open invoices'}
          />
          <StatCard
            icon={<Clock className="h-5 w-5" />}
            label="Never chased"
            value={wsError ? '—' : stats.neverChased}
            hint={wsError ? "Couldn't load" : stats.neverChased > 0 ? 'invoices with no contact yet' : 'all chased at least once'}
          />
          <StatCard
            icon={<Warning className="h-5 w-5" />}
            label="61+ days overdue"
            value={
              wsError ? '—'
                : stats.critical > 0
                ? `£${stats.critical.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
                : '£0'
            }
            hint={wsError ? "Couldn't load" : stats.critical > 0 ? 'escalate immediately' : 'none critical'}
          />
        </div>

        {/* Tab bar */}
        <div className="flex gap-0 border-b border-border">
          {([
            { key: 'workspace', label: 'Recovery workspace' },
            { key: 'aged-debt', label: 'Aged debt by client' },
          ] as { key: Tab; label: string }[]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ─── Recovery workspace tab ─── */}
        {tab === 'workspace' && (
          <Card>
            <div className="flex items-center justify-between border-b border-border px-6 py-3">
              <span className="text-sm text-muted-foreground">
                Per-invoice view · sorted by days overdue (worst first)
              </span>
              <span className="text-sm text-muted-foreground">{items.length} invoice{items.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Matter</th>
                    <th>Client</th>
                    <th className="text-right">Outstanding</th>
                    <th className="text-right">Days overdue</th>
                    <th>Last chased</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {wsLoading && (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                        Loading recovery workspace…
                      </td>
                    </tr>
                  )}
                  {wsError && (
                    <tr><td colSpan={8}><QueryErrorState what="the recovery workspace" onRetry={() => wsRefetch()} /></td></tr>
                  )}
                  {!wsLoading && !wsError && items.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-12 text-center">
                        <CheckCircle className="mx-auto h-8 w-8 text-emerald-500" weight="fill" />
                        <p className="mt-2 text-sm font-semibold text-foreground">No outstanding invoices</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          All fee notes are paid, draft, or not yet due.
                        </p>
                      </td>
                    </tr>
                  )}
                  {items.map((item) => (
                    <tr key={item.feeNoteId}>
                      <td>
                        <span className="font-mono text-xs text-foreground">{item.feeNoteNumber}</span>
                      </td>
                      <td>
                        <button
                          className="text-sm font-medium text-primary hover:underline"
                          onClick={() => navigate(`${basePath}/matters/${item.caseId}`)}
                        >
                          {item.caseName}
                        </button>
                        <p className="text-xs text-muted-foreground">{item.caseNumber}</p>
                      </td>
                      <td className="text-sm text-foreground">{item.clientName}</td>
                      <td className="text-right font-semibold text-foreground">
                        {fmt(item.outstanding)}
                      </td>
                      <td className={`text-right font-medium tabular-nums ${
                        item.daysOverdue > 60 ? 'text-red-700 dark:text-red-300' :
                        item.daysOverdue > 30 ? 'text-amber-700 dark:text-amber-300' :
                        'text-muted-foreground'
                      }`}>
                        {item.daysOverdue > 0 ? `${item.daysOverdue}d` : <span className="text-muted-foreground">not yet</span>}
                      </td>
                      <td>
                        {item.lastChased ? (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            {methodIcon(item.lastChased.method)}
                            <span>{fmtDate(item.lastChased.contactedAt)}</span>
                            {item.chaseCount > 1 && (
                              <span className="text-muted-foreground/60">·{item.chaseCount}×</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td>{chaseBadge(item)}</td>
                      <td>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => setChasingItem(item)}
                        >
                          Log chase
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* ─── Aged debt tab ─── */}
        {tab === 'aged-debt' && (
          <Card>
            <div className="flex items-center justify-between border-b border-border px-6 py-3">
              <span className="text-sm text-muted-foreground">
                Aged debt by client · days past invoice due date
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="text-right">Matters</th>
                    <th className="text-right">Not yet due</th>
                    <th className="text-right">1–30 days</th>
                    <th className="text-right">31–60 days</th>
                    <th className="text-right">61+ days</th>
                    <th className="text-right">Total overdue</th>
                    <th>Risk</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {adLoading && (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-sm text-muted-foreground">
                        Loading aged debt…
                      </td>
                    </tr>
                  )}
                  {adError && (
                    <tr><td colSpan={9}><QueryErrorState what="aged debt" onRetry={() => adRefetch()} /></td></tr>
                  )}
                  {!adLoading && !adError && agedRows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-12 text-center">
                        <CheckCircle className="mx-auto h-8 w-8 text-emerald-500" weight="fill" />
                        <p className="mt-2 text-sm font-semibold text-foreground">No outstanding balances</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          All fee notes are paid, draft, or not yet due.
                        </p>
                      </td>
                    </tr>
                  )}
                  {agedRows.map((row) => {
                    const overdueTotal = row.d30 + row.d60 + row.d90;
                    // Held in a local so the null check below narrows inside the click handler.
                    const contactId = row.contactId;
                    return (
                      <tr key={row.contactId ?? row.clientName}>
                        <td className="font-medium text-foreground">{row.clientName}</td>
                        <td className="text-right text-muted-foreground">{row.mattersCount}</td>
                        <td className="text-right text-muted-foreground">{fmt(row.current)}</td>
                        <td className="text-right text-muted-foreground">{fmt(row.d30)}</td>
                        <td className={`text-right ${row.d60 > 0 ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
                          {fmt(row.d60)}
                        </td>
                        <td className={`text-right ${row.d90 > 0 ? 'font-semibold text-red-700 dark:text-red-300' : 'text-muted-foreground'}`}>
                          {fmt(row.d90)}
                        </td>
                        <td className={`text-right font-semibold ${overdueTotal > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {fmt(overdueTotal)}
                        </td>
                        <td>{agedRiskBadge(row)}</td>
                        <td>
                          {contactId && (
                            <Button
                              variant="ghost"
                              size="xs"
                              rightIcon={<ArrowSquareOut className="h-3.5 w-3.5" />}
                              onClick={() => navigate(contactPath(contactId))}
                            >
                              View client
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {agedRows.length > 1 && (
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/20">
                      <td className="px-4 py-3 text-sm font-semibold text-foreground" colSpan={2}>Totals</td>
                      <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                        {fmt(agedRows.reduce((s, r) => s + r.current, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                        {fmt(agedRows.reduce((s, r) => s + r.d30, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-amber-700 dark:text-amber-300">
                        {fmt(agedRows.reduce((s, r) => s + r.d60, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-red-700 dark:text-red-300">
                        {fmt(agedRows.reduce((s, r) => s + r.d90, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-foreground">
                        {fmt(agedRows.reduce((s, r) => s + r.d30 + r.d60 + r.d90, 0))}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
